import {Controller} from "@hotwired/stimulus"

interface KlaroManager {
    getConsent(name: string): boolean | undefined
    updateConsent(name: string, value: boolean | undefined): void
    saveConsents(): void
    watch(watcher: { update: (obj: any, name: string, data: any) => void }): void
    _isWatchedByJuraclimbs?: boolean
}

interface Klaro {
    getManager(config: any): KlaroManager
    show(config: any, modal: boolean): void
}

declare global {
    interface Window {
        klaro: Klaro
        klaroConfig: any
    }
}

export default class YoutubeConsentController extends Controller {
    static targets = ["placeholder", "iframeTemplate"]
    static values = {hasDbConsent: Boolean, updateUrl: String}

    declare readonly placeholderTarget: HTMLElement
    declare readonly iframeTemplateTarget: HTMLTemplateElement
    declare readonly hasIframeTemplateTarget: boolean
    declare hasDbConsentValue: boolean
    declare readonly updateUrlValue: string
    declare readonly hasUpdateUrlValue: boolean

    connect(): void {
        console.log('youtube-consent: connect() called')

        if (typeof window.klaro === 'undefined' || !window.klaro.getManager(window.klaroConfig)) {
            console.log('youtube-consent: Klaro NOT ready, waiting...')
            setTimeout(() => this.connect(), 100)
            return
        }

        console.log('youtube-consent: Klaro ready')

        this.watchConsent()

        const hasDbConsent = this.hasDbConsentValue
        const hasKlaroConsent = this.getKlaroConsent()
        console.log('youtube-consent: hasDbConsentValue:', hasDbConsent)
        console.log('youtube-consent: getKlaroConsent():', hasKlaroConsent)

        if (this.placeholderTarget.querySelector('iframe')) {
            console.log('youtube-consent: Video already present, skipping')
            return
        }

        if (hasDbConsent || hasKlaroConsent) {
            console.log('youtube-consent: Loading video because consent is TRUE')
            this.loadVideo()
        } else {
            console.log('youtube-consent: Video NOT loading because consent is FALSE')
        }
    }

    getKlaroConsent(): boolean {
        if (typeof window.klaro === 'undefined') return false
        const manager = window.klaro.getManager(window.klaroConfig)
        if (!manager) return false

        const consent = manager.getConsent('youtube')
        console.log('youtube-consent: manager.getConsent("youtube") is:', consent)

        return consent === true
    }

    showBanner(event: Event): void {
        console.log('youtube-consent: showBanner() called')
        event.preventDefault()

        const manager = window.klaro.getManager(window.klaroConfig)
        if (manager) {
            console.log('youtube-consent: manager found, checking state...')
            const currentConsent = manager.getConsent('youtube')

            if (currentConsent !== true) {
                console.log('youtube-consent: consent was NOT TRUE, reset and show modal')
                manager.updateConsent('youtube', undefined)
                manager.saveConsents()
            }

            console.log('youtube-consent: opening Klaro modal via window.klaro.show()')
            window.klaro.show(window.klaroConfig, true)
        } else {
            console.log('youtube-consent: NO manager, opening Klaro via window.klaro.show()')
            window.klaro.show(window.klaroConfig, true)
        }

        const removeHiddenClasses = () => {
            const dialog = window.document.querySelector("div#klaro .cookie-notice") as HTMLElement | null
            if (dialog) {
                dialog.classList.remove('cookie-notice-hidden')
                console.log('Klasse cookie-notice-hidden wurde manuell entfernt')
            }

            const modal = window.document.querySelector("div#klaro .cookie-modal") as HTMLElement | null
            if (modal) {
                modal.classList.remove('cookie-modal-hidden')
                modal.style.display = 'flex'
                console.log('Klasse cookie-modal-hidden wurde manuell entfernt und display auf flex gesetzt')
            }
        }

        setTimeout(removeHiddenClasses, 50)
        setTimeout(removeHiddenClasses, 300)
        setTimeout(removeHiddenClasses, 1000)
    }

    watchConsent(): void {
        const manager = window.klaro.getManager(window.klaroConfig)

        if (manager._isWatchedByJuraclimbs) return
        manager._isWatchedByJuraclimbs = true

        console.log('youtube-consent: Registering watcher via manager.watch')

        manager.watch({
            update: (_obj: any, name: string, _data: any) => {
                console.log('youtube-consent: Klaro manager.watch update:', name)
                if (name === 'consents' || name === 'consent') {
                    console.log('youtube-consent: Consent changed, updating controllers...')
                    this.updateAllControllers()
                }
            }
        })
    }

    updateAllControllers(): void {
        const controllers = Array.from(document.querySelectorAll('[data-controller="youtube-consent"]'))
            .map(el => this.application.getControllerForElementAndIdentifier(el, 'youtube-consent') as YoutubeConsentController)
            .filter(c => c)

        console.log(`youtube-consent: found ${controllers.length} controllers to update`)

        for (const controller of controllers) {
            const hasKlaro = controller.getKlaroConsent()
            console.log('youtube-consent: checking controller, getKlaroConsent():', hasKlaro)
            if (hasKlaro) {
                controller.loadVideo()
                if (controller.hasUpdateUrlValue && !controller.hasDbConsentValue) {
                    console.log('youtube-consent: Klaro consent given and NO DB consent yet, saving to DB...')
                    controller.saveToDb(true).then(() => {
                        controller.hasDbConsentValue = true
                    })
                }
            } else {
                console.log('youtube-consent: Klaro consent NOT given, removing video...')
                controller.placeholderTarget.innerHTML = '<div data-youtube-consent-target="placeholder"><div class="bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg aspect-video flex flex-col items-center justify-center p-6 text-center"></div></div>'
                controller.saveToDb(false).then(() => {
                    controller.hasDbConsentValue = false
                })
            }
        }
    }

    loadVideo(): void {
        if (this.hasIframeTemplateTarget) {
            console.log('youtube-consent: Copying iframe from template to placeholder')
            const content = this.iframeTemplateTarget.content.cloneNode(true)
            this.placeholderTarget.innerHTML = ''
            this.placeholderTarget.appendChild(content)
        } else {
            console.error('youtube-consent: iframeTemplate target not found')
        }
    }

    async saveToDb(hasConsent: boolean): Promise<void> {
        console.log('youtube-consent: saveToDb called, updateUrl:', this.updateUrlValue)
        try {
            const response = await fetch(this.updateUrlValue, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({youtube_consent: hasConsent})
            })
            if (!response.ok) {
                const errorText = await response.text()
                console.error('youtube-consent: saveToDb failed with status:', response.status, errorText)
                return
            }
            const data = await response.json()
            console.log('youtube-consent: saveToDb response:', data)
        } catch (error) {
            console.error('youtube-consent: saveToDb error:', error)
        }
    }
}
